// Constants
const MOBILE_BREAKPOINT = 768;

// State management
const State = {
  currentExpanded: null,
  isMobile: window.innerWidth <= MOBILE_BREAKPOINT
};

// DOM Elements cache
const DomElements = {
  get container() {
    return document.querySelector('.product-info-cards');
  },
  get cardsContainer() {
    return document.querySelector('.info-cards-container');
  },
  get cards() {
    return document.querySelectorAll('.info-card');
  },
  get deliveryLists() {
    // Puede haber 2: la cara desktop (.card-back) y la vista móvil (.mobile-view)
    return document.querySelectorAll('.delivery-list');
  },
  get printButtons() {
    return document.querySelectorAll('.print-warranty-btn, .mobile-print-btn');
  }
};

// Configuration from data attributes
const Config = {
  get countryCode() {
    return DomElements.container?.dataset.countryCode || 'SV';
    console.log("DomElements.container?.dataset.countryCode")
  },
  
  get deliveryJsonUrl() {
    return DomElements.container?.dataset.deliveryJsonUrl || '/assets/delivery.json';
  }
};

// Mobile detection
const MobileDetector = {
  check() {
    State.isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
  },
  
  isMobile() {
    return State.isMobile;
  }
};

// Layout management
const LayoutManager = {
  expandCard(card) {
    const cardType = card.getAttribute('data-card');
    
    this.reset();
    State.currentExpanded = cardType;
    
    DomElements.cardsContainer.classList.add(`${cardType}-expanded`);
    card.classList.add('expanded');
    
    // Update accessibility
    Accessibility.updateCardAccessibility(card, true);
  },
  
  reset() {
    // Reset container classes
    DomElements.cardsContainer.className = 'info-cards-container';
    
    // Reset all cards
    DomElements.cards.forEach(card => {
      card.classList.remove('expanded', 'active');
      Accessibility.updateCardAccessibility(card, false);
    });
    
    State.currentExpanded = null;
  },
  
  toggleCard(card) {
    if (!MobileDetector.isMobile()) {
      const isActive = card.classList.toggle('active');
      Accessibility.updateCardAccessibility(card, isActive);
      return;
    }
    
    const cardType = card.getAttribute('data-card');
    
    if (State.currentExpanded === cardType) {
      this.reset();
    } else {
      this.expandCard(card);
    }
  }
};

// Delivery information service
const DeliveryService = {
  async loadDeliveryInfo() {
    try {
      console.log('Cargando información de entrega para:', Config.countryCode);
      console.log('URL del JSON:', Config.deliveryJsonUrl);
      
      const response = await fetch(Config.deliveryJsonUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data[Config.countryCode]) {
        console.warn(`No delivery info found for country: ${Config.countryCode}`);
        this.showFallbackDeliveryInfo();
        return;
      }
      
      this.renderDeliveryInfo(data[Config.countryCode].infoDelivery[0]);
    } catch (error) {
      console.error('Error cargando JSON de entrega:', error);
      this.showFallbackDeliveryInfo();
    }
  },
  
  renderDeliveryInfo(info) {
    const lists = DomElements.deliveryLists;
    if (!lists.length) {
      console.warn('Elemento .delivery-list no encontrado');
      return;
    }

    const html = this.generateDeliveryHTML(info);
    lists.forEach(list => { list.innerHTML = html; });
  },
  
  generateDeliveryHTML(info) {
    let html = '';
    
    // Delivery zones
    if (info.zonas && info.zonas.length > 0) {
      info.zonas.forEach(zone => {
        html += `<li>${this.escapeHTML(zone.zona)}: ${this.escapeHTML(zone.promesaEntrega)}</li>`;
      });
    }
    
    // Shipping costs
    if (info.tarifas?.general) {
      html += `<li>Costo de envío: ${this.escapeHTML(info.tarifas.general)}</li>`;
    }
    
    // Preparation section
    if (info.sections) {
      const preparationSection = info.sections.find(section => 
        section.toLowerCase().includes('preparación')
      );
      
      if (preparationSection) {
        html += `<li>${this.escapeHTML(preparationSection)}</li>`;
      }
    }
    
    return html || '<li>Información de entrega no disponible</li>';
  },
  
  showFallbackDeliveryInfo() {
    const lists = DomElements.deliveryLists;
    if (!lists.length) return;

    const html = `
      <li>Zona Central: 2-3 días hábiles</li>
      <li>Zona Norte/Sur: 3-5 días hábiles</li>
      <li>Costo de envío: Gratis sobre $50.000</li>
    `;
    lists.forEach(list => { list.innerHTML = html; });
  },
  
  escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
};

// Print service
const PrintService = {
  printWarranty() {
    const warrantyUrl = '/pages/garantia-completa';
    const printWindow = window.open(warrantyUrl, '_blank', 'width=800,height=600');
    
    if (printWindow) {
      printWindow.onload = () => {
        try {
          printWindow.print();
        } catch (error) {
          console.error('Error al imprimir:', error);
          printWindow.close();
        }
      };
    } else {
      window.open(warrantyUrl, '_blank');
    }
  }
};

// Event handlers
const EventHandlers = {
  handleCardClick(card) {
    LayoutManager.toggleCard(card);
  },
  
  handleCardKeydown(e, card) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleCardClick(card);
    }
  },
  
  handleResize() {
    const wasMobile = State.isMobile;
    MobileDetector.check();
    
    if (wasMobile !== State.isMobile) {
      LayoutManager.reset();
    }
  },
  
  handlePrintButtonClick(e) {
    e.stopPropagation();
    PrintService.printWarranty();
  }
};

// Accessibility utilities
const Accessibility = {
  setupCardAccessibility(card) {
    card.setAttribute('tabindex', '0');
    card.setAttribute('role', 'button');
    card.setAttribute('aria-expanded', 'false');
    card.setAttribute('aria-label', `Más información sobre ${card.querySelector('.card-title')?.textContent || 'esta sección'}`);
  },
  
  updateCardAccessibility(card, isExpanded) {
    card.setAttribute('aria-expanded', isExpanded.toString());
  }
};

// Initialization
const App = {
  init() {
    // Verificar que el contenedor principal existe
    if (!DomElements.container) {
      console.warn('Elemento .product-info-cards no encontrado');
      return;
    }
    
    this.setupCards();
    this.setupEventListeners();
    this.setupPrintButtons();
    DeliveryService.loadDeliveryInfo();
    
    console.log('Info Cards app inicializada para país:', Config.countryCode);
  },
  
  setupCards() {
    DomElements.cards.forEach(card => {
      Accessibility.setupCardAccessibility(card);
      
      card.addEventListener('click', () => {
        EventHandlers.handleCardClick(card);
      });
      
      card.addEventListener('keydown', (e) => {
        EventHandlers.handleCardKeydown(e, card);
      });
    });
  },
  
  setupPrintButtons() {
    DomElements.printButtons.forEach(button => {
      button.addEventListener('click', EventHandlers.handlePrintButtonClick);
    });
  },
  
  setupEventListeners() {
    window.addEventListener('resize', () => {
      EventHandlers.handleResize();
    });
    
    // Prevent card toggle when clicking inside card content
    document.querySelectorAll('.card-back, .mobile-expanded').forEach(element => {
      element.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    });
  }
};

// Make PrintService globally available for legacy support
window.printWarranty = PrintService.printWarranty;

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    App.init();
  });
} else {
  App.init();
}